import { useEffect, useState, useRef, useCallback } from "react"; // Added useCallback
import { Position } from "reactflow"; // Import Position enum directly
import { DestinationNodeData, HandleConfig } from "../../types"; // Removed Position from here
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager"; // Import HandleManager
// Import the shared suggestions
import { CUSTOM_LABEL_SUGGESTIONS, STATUS_SUGGESTIONS } from "../../data/suggestions";


interface DestinationConfigBoxProps {
  nodeId: string; // Add nodeId prop
  data: DestinationNodeData;
  onUpdate?: (data: Partial<DestinationNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void;
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

export default function DestinationConfigBox({
  nodeId, // Destructure nodeId
  data,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup,
  portalRoot,
  isAdvancedMode = false,
}: DestinationConfigBoxProps) {
  // Initialize state with props
  const [label, setLabel] = useState(data.label || "");
  const [status, setStatus] = useState(data.status || "");
  const [customLabel, setCustomLabel] = useState(data.customLabel || "");
  const [editableLatencyImpact, setEditableLatencyImpact] = useState<string>(
    (data.latencyImpact ?? '').toString()
  );
  const [notes, setNotes] = useState(data.notes || "");

  // --- Default Handle for Backward Compatibility ---
  const defaultHandle: HandleConfig = {
    id: 'default-target', // Match the ID used in DestinationNode fallback
    type: 'target',
    position: Position.Left,
    color: '#34D399', // Use the green color from the original handle
  };
  // --- End Default Handle ---

  // --- Add State for Handles ---
  const [handles, setHandles] = useState<HandleConfig[]>(() => {
    if (Array.isArray(data.handles)) {
      // Use data.handles if it's an array (can be empty)
      return [...data.handles];
    } else {
      // Use default only if data.handles is undefined/null (old node)
      return [defaultHandle];
    }
  });
  // --- End Add State for Handles ---

  // --- State to track if the node is an old version (lacks handles array) ---
  const [isOldVersion, setIsOldVersion] = useState<boolean>(() => !Array.isArray(data.handles));
  // --- End State ---

  // Initialize visibility settings
  const [statusVisibility, setStatusVisibility] = useState<VisibilityOption>(
    data.statusVisibility === "hidden" ? "hidden" : "visible"
  );
  const [customLabelVisibility, setCustomLabelVisibility] = useState<VisibilityOption>(
    data.customLabelVisibility === "hidden" ? "hidden" : "visible"
  );
  // --- Calculate initial visibility directly based on the FIRST data prop ---
  const calculateInitialVisibility = (nodeData: DestinationNodeData) => {
      const hasValidImpact = typeof nodeData.latencyImpact === 'number' && nodeData.latencyImpact > 0;
      const isExplicitlyHiddenInData = nodeData.latencyImpactVisibility === 'hidden';
      return !hasValidImpact || isExplicitlyHiddenInData ? 'hidden' : 'visible';
  };
  // Initialize states directly using the calculation function
  const [latencyImpactVisibility, setLatencyImpactVisibility] = useState<VisibilityOption>(() => calculateInitialVisibility(data));

  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(data.notesVisibility ?? "visible");

  // Track previous node ID to detect changes
  const prevNodeIdRef = useRef<string | null>(null);

  // Track previous update to prevent loops and for change detection
  const prevUpdateRef = useRef("");

  // Flag to prevent updates during state synchronization
  const isSyncingRef = useRef(false);

  // Effect to synchronize internal state with the data prop
  useEffect(() => {
    const currentNodeId = (data as any).id || JSON.stringify(data); // Use ID if available

    // Update general node properties ONLY if the node ID actually changes
    if (prevNodeIdRef.current !== currentNodeId) {
      // console.log(`[DestinationConfigBox useEffect] Node changed: ${prevNodeIdRef.current} -> ${currentNodeId}`);
      setLabel(data.label || "");
      setStatus(data.status || "");
      setCustomLabel(data.customLabel || "");
      setStatusVisibility(data.statusVisibility === "hidden" ? "hidden" : "visible");
      setCustomLabelVisibility(data.customLabelVisibility === "hidden" ? "hidden" : "visible");
      const correctVisibility = calculateInitialVisibility(data);
      setLatencyImpactVisibility(correctVisibility);

      setEditableLatencyImpact((data.latencyImpact ?? '').toString());
      setNotes(data.notes || "");
      setNotesVisibility(data.notesVisibility ?? "visible");
      // Initialize handles: use data.handles if it's an array (even empty), otherwise use default
      const handlesExist = Array.isArray(data.handles);
      setIsOldVersion(!handlesExist); // Update old version status
      if (handlesExist && data.handles) {
        setHandles([...data.handles]);
      } else {
        setHandles([defaultHandle]);
      }
      prevNodeIdRef.current = currentNodeId; // Update ref *after* setting state based on ID change

      // Use setTimeout to reset the flag after state updates likely settle
      // and reset the prevUpdateRef to the newly synced state
      setTimeout(() => {
        isSyncingRef.current = false; // End sync
        // Reset prevUpdateRef *after* syncing to allow the first user change to trigger an update
        prevUpdateRef.current = JSON.stringify({
          label: data.label || "",
          status: data.status || "",
          customLabel: data.customLabel || "",
          statusVisibility: data.statusVisibility === "hidden" ? "hidden" : "visible",
          customLabelVisibility: data.customLabelVisibility === "hidden" ? "hidden" : "visible",
          latencyImpactVisibility: calculateInitialVisibility(data),
          latencyImpact: data.latencyImpact ?? null,
          notes: data.notes || "",
          notesVisibility: data.notesVisibility ?? "visible",
          // Use the refined logic for resetting prevUpdateRef's handles
          handles: Array.isArray(data.handles) ? [...data.handles] : [defaultHandle],
        });
      }, 0);
    } else {
      // Node ID is the same, but data reference might have changed (e.g., handles update from store)
      // Determine the handles to sync based on incoming data, respecting empty arrays
      let handlesToSync: HandleConfig[];
      if (Array.isArray(data.handles)) {
        // If data.handles is an array (even empty), use it
        handlesToSync = [...data.handles];
      } else {
        // If data.handles is undefined or null (old node), use the default
        handlesToSync = [defaultHandle];
      }

      // Only update internal state if the determined handles differ from current state
      if (JSON.stringify(handlesToSync) !== JSON.stringify(handles)) {
          // isSyncingRef.current = true; // Potentially needed
          setHandles(handlesToSync);
          // setTimeout(() => { isSyncingRef.current = false; }, 0); // If flag used
      }
    }
  }, [data, defaultHandle]); // Add defaultHandle to dependencies as it's used in initialization logic

  // Toggle visibility handlers
  const toggleStatusVisibility = () => {
    setStatusVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  const toggleCustomLabelVisibility = () => {
    setCustomLabelVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  // Add toggle handler for latency impact visibility
  const toggleLatencyImpactVisibility = () => {
    setLatencyImpactVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  // Toggle visibility setting for notes
  const toggleNotesVisibility = () => {
    setNotesVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  // --- Handler for HandleManager changes ---
  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    // Allow setting empty handles
    setHandles(newHandles);
  }, []); // Dependency array is empty as setHandles is stable
  // --- End Handler ---

  // Update parent when state changes (triggered by user interaction)
  useEffect(() => {
    // Prevent updates during sync, if no node selected, or no update handler
    if (isSyncingRef.current || !prevNodeIdRef.current || !onUpdate) return;

    // Parse the editable latency impact string into a number or null
    const parsedLatencyImpact = editableLatencyImpact === '' ? null : parseInt(editableLatencyImpact, 10);
    const currentLatencyImpact = isNaN(parsedLatencyImpact as number) ? null : parsedLatencyImpact;

    const updates: Partial<DestinationNodeData> = {
      label,
      status: status as DestinationNodeData['status'],
      customLabel,
      statusVisibility,
      customLabelVisibility,
      latencyImpactVisibility, // Current state of the toggle
      latencyImpact: currentLatencyImpact, // Current parsed value from input
      notes,
      notesVisibility,
      handles, // Include handles in the update payload
    };

    // --- Change Detection Logic ---
    // We now compare the stringified 'updates' object directly with the last known state ('prevUpdateRef').
    // No need for the intermediate 'hasChanged' variable comparing internal state vs props.
    // --- End Change Detection Logic ---

    const updatesJson = JSON.stringify(updates); // Stringify includes handles now

    // Only call onUpdate if the current state (updatesJson) is different from the last known state (prevUpdateRef)
    if (updatesJson !== prevUpdateRef.current) {
      // console.log("[DestinationConfigBox] Change detected, calling onUpdate", updates);
      prevUpdateRef.current = updatesJson; // Update ref *before* calling onUpdate
      onUpdate(updates);
    }
  }, [
    label, 
    status,
    customLabel,
    statusVisibility,
    customLabelVisibility,
    latencyImpactVisibility, // Current state of the toggle
    editableLatencyImpact, // Current value in the input
    notes,
    notesVisibility,
    handles, // Include handles in the dependency array
    onUpdate // Include onUpdate callback

    // Change detection now relies on comparing updatesJson with prevUpdateRef.
  ]);

  // No longer need formattedLatencyImpact for display, using input value directly

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Destination Name
        </label>
        <input
          type="text"
          value={label}
          onChange={e => setLabel(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Status field with visibility toggle */}
      <div>
        <LabelWithVisibilityToggle
          label="Status"
          visibility={statusVisibility}
          onToggleVisibility={toggleStatusVisibility}
          className="mb-1"
        />
        <AutoComplete
          value={status}
          onChange={setStatus}
          options={STATUS_SUGGESTIONS}
          placeholder="Set a Status Label"
          dropdownClassName="absolute z-[9999]"
          portalContainer={portalRoot}
        />
      </div>
      
      {/* Custom Label field with visibility toggle */}
      <div>
        <LabelWithVisibilityToggle
          label="Custom Label"
          visibility={customLabelVisibility}
          onToggleVisibility={toggleCustomLabelVisibility}
          className="mb-1"
        />
        <AutoComplete
          value={customLabel}
          onChange={setCustomLabel}
          options={CUSTOM_LABEL_SUGGESTIONS}
          placeholder="Set a Custom Label"
          dropdownClassName="absolute z-[9999]"
          portalContainer={portalRoot}
        />
      </div>

      {/* Latency Impact Display and Visibility Toggle */}
      <div>
        <LabelWithVisibilityToggle
          label="Latency Impact"
          visibility={latencyImpactVisibility}
          onToggleVisibility={toggleLatencyImpactVisibility}
          className="mb-1"
        />
        <div className="relative">
          <input
            type="number"
            value={editableLatencyImpact}
            onChange={e => setEditableLatencyImpact(e.target.value)}
            min="0"
            step="1"
            placeholder="e.g., 50"
            className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">
            ms
          </span>
        </div>
      </div>

      {/* Notes Section */}
      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={toggleNotesVisibility}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this destination (optional)"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[80px] max-h-[400px]"
          maxLength={500} // Limit to 500 characters
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/500
        </div>
      </div>

      {/* --- Conditionally Render Handle Manager --- */}
      {/* Only show HandleManager if it's NOT an old version node */}
      {!isOldVersion && (
        <div>
          <HandleManager
            nodeId={nodeId} // Use the nodeId prop
            handles={handles}
            onChange={handleHandlesChange}
            maxHandles={6}
            portalRoot={portalRoot}
          />
        </div>
      )}
      {/* --- End Handle Manager --- */}
    </div>
  );
}
